// 品番原価グリッドの行/列の組み立てと表示値の整形をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createGenkaGridService(deps) {
    const {
        DicVal,
        GridData,
        HINBAN_GENKA_COLUMN_MAP,
        HINBAN_GENKA_PANEL_TYPE,
        HINBAN_GENKA_ROW_DEFS,
        HINBAN_GENKA_SLOT_DEFS,
        HINBAN_GENKA_SUMMARY_DEFS,
        columninfo,
        ensureGridDataRef,
        isEmpty
    } = deps;

    function normalizeHinbanGenkaUpper(value) {
        return String(value ?? '').trim().toUpperCase();
    }
    function mapHinbanGenkaByColumnId(columnId) {
        const key = normalizeHinbanGenkaUpper(columnId);
        return key === '' ? null : DicVal(HINBAN_GENKA_COLUMN_MAP, key, null);
    }
    function emptyHinbanGenkaRow(def, index) {
        return { rowid: String(DicVal(def, 'key', index)), row_key: DicVal(def, 'key', ''), item_name: DicVal(def, 'label', ''), muryo: '', muryo_f: '', yusho: '', yusho_f: '', kojo: '', kojo_f: '', syokei: '', syokei_f: '', kousei: '' };
    }
    function rawHinbanGenkaRow(sourceRows) {
        return Array.isArray(sourceRows) && sourceRows.length !== 0 && !isEmpty(sourceRows[0]) ? sourceRows[0] : {};
    }
    function buildHinbanGenkaDisplayRows(sourceRows) {
        const rawRow = rawHinbanGenkaRow(sourceRows);
        const rowMap = {};
        HINBAN_GENKA_ROW_DEFS.forEach((def, index) => { rowMap[def.key] = emptyHinbanGenkaRow(def, index); });
        Object.keys(rawRow).forEach((columnId) => {
            const mapped = mapHinbanGenkaByColumnId(columnId);
            if (isEmpty(mapped)) return;
            const row = DicVal(rowMap, DicVal(mapped, 'row', ''), null);
            if (isEmpty(row)) return;
            const value = String(DicVal(rawRow, columnId, '') ?? '');
            (Array.isArray(mapped.cols) ? mapped.cols : []).forEach((slot) => { if (slot !== '') row[slot] = value; });
        });
        return HINBAN_GENKA_ROW_DEFS.map((def, index) => rowMap[def.key] || emptyHinbanGenkaRow(def, index));
    }
    function fixedHinbanGenkaDisplayRows(sourceRows) {
        const rows = buildHinbanGenkaDisplayRows(sourceRows);
        const byKey = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const key = String(DicVal(row, 'row_key', '')).trim();
            if (key !== '') byKey[key] = row;
        });
        return HINBAN_GENKA_ROW_DEFS.map((def, index) => ({
            ...emptyHinbanGenkaRow(def, index),
            ...(DicVal(byKey, def.key, {}) || {})
        }));
    }
    function buildHinbanGenkaSaveRows(displayRows, previousRows) {
        const base = { ...(rawHinbanGenkaRow(previousRows) || {}) };
        if (isEmpty(base.rowid)) base.rowid = '0';
        const rowMap = {};
        (Array.isArray(displayRows) ? displayRows : []).forEach((row) => { const rowKey = String(DicVal(row, 'row_key', '') ?? '').trim(); if (rowKey !== '') rowMap[rowKey] = row; });
        Object.keys(HINBAN_GENKA_COLUMN_MAP).forEach((columnId) => {
            const mapped = HINBAN_GENKA_COLUMN_MAP[columnId];
            const row = DicVal(rowMap, DicVal(mapped, 'row', ''), null);
            const slots = Array.isArray(mapped.cols) ? mapped.cols : [];
            let value = '';
            if (!isEmpty(row)) { for (let i = 0; i < slots.length; i++) { const nextValue = String(DicVal(row, slots[i], '') ?? ''); if (nextValue !== '') { value = nextValue; break; } } }
            base[columnId] = value;
        });
        return [base];
    }
    function isHinbanGenkaDisplayRows(rows) {
        return Array.isArray(rows) && rows.some((row) => !isEmpty(DicVal(row, 'row_key', '')));
    }
    function buildHinbanGenkaStoredRows(panelInfo, rows) {
        if (!isHinbanGenkaDisplayRows(rows)) return rows;
        return buildHinbanGenkaSaveRows(rows, GridData(panelInfo));
    }
    function resolveHinbanGenkaSlotLayoutMeta(slot) {
        const headers = { muryo: ['無償支給'], muryo_f: ['無償支給(外)', '無償支給（外）'], yusho: ['有償支給'], yusho_f: ['有償支給(外)', '有償支給（外）'], kojo: ['工場手配'], kojo_f: ['工場手配(外)', '工場手配（外）'], syokei: ['小計'], syokei_f: ['小計(外)', '小計（外）'], kousei: ['構成比率'] };
        const matched = (Array.isArray(columninfo) ? columninfo : []).filter((item) => String(DicVal(item, 'type', '')).trim() === HINBAN_GENKA_PANEL_TYPE).find((item) => {
            const mapped = mapHinbanGenkaByColumnId(DicVal(item, 'columnid', ''));
            if (!isEmpty(mapped) && Array.isArray(mapped.cols) && mapped.cols.includes(slot)) return true;
            return DicVal(headers, slot, []).includes(String(DicVal(item, 'columnnm', '')).trim());
        });
        return { width: Number(DicVal(matched, 'width', 0)), colinfo: isEmpty(matched) ? null : { ...matched } };
    }
    function formatHinbanGenkaDisplayValue(params) {
        const raw = String(DicVal(params, 'value', '') ?? '').trim();
        if (raw === '' || raw === '0') return '';
        return raw;
    }
    function formatHinbanGenkaKouseiDisplayValue(params) {
        const raw = String(DicVal(params, 'value', '') ?? '').trim();
        if (raw === '') return '';
        const n = Number(raw);
        if (!Number.isFinite(n)) return raw;
        if (n === 0) return '';
        return String(Math.round(n)) + '%';
    }
    function hinbanGenkaSlotColInfo(rowKey, slot) {
        const mappedColumnIds = Object.keys(HINBAN_GENKA_COLUMN_MAP).filter((columnId) => {
            const mapped = HINBAN_GENKA_COLUMN_MAP[columnId];
            return String(DicVal(mapped, 'row', '')).trim() === String(rowKey).trim() && (Array.isArray(mapped.cols) ? mapped.cols : []).includes(slot);
        });
        return mappedColumnIds
            .map((columnId) => (Array.isArray(columninfo) ? columninfo : []).find((item) => String(DicVal(item, 'type', '')).trim() === HINBAN_GENKA_PANEL_TYPE && String(DicVal(item, 'columnid', '')).trim().toUpperCase() === columnId))
            .find((item) => !isEmpty(item));
    }
    function isHinbanGenkaCellEditable(rowKey, slot) {
        if (slot === '' || slot === 'item_name' || slot === 'syokei' || slot === 'syokei_f' || slot === 'kousei') return false;
        const cinfo = hinbanGenkaSlotColInfo(rowKey, slot);
        if (isEmpty(cinfo)) return false;
        if (DicVal(cinfo, 'locked', false) || DicVal(cinfo, 'unchange', false)) return false;
        return true;
    }
    function hinbanGenkaInactiveCellStyle(params, isLabel) {
        const rowKey = String(DicVal(DicVal(params, 'data', {}), 'row_key', '')).trim();
        const colId = isLabel ? 'item_name' : String(DicVal(DicVal(params, 'colDef', {}), 'colId', '') || DicVal(DicVal(params, 'colDef', {}), 'field', '')).trim();
        if (isLabel) return { backgroundColor: '#8fa4a3', color: '#ffffff', fontWeight: 600, textAlign: 'left' };
        if (isHinbanGenkaCellEditable(rowKey, colId)) return null;
        return { backgroundColor: '#eef1f1', color: '#2f3a4a', textAlign: 'right' };
    }
    function buildHinbanGenkaFixedColumns() {
        return [
            { colId: 'item_name', field: 'item_name', headerName: '項目名', minWidth: 122, flex: 1.35, colinfo: null, editable: false, sortable: false, filter: false, resizable: false, suppressMenu: true, lockPosition: true, suppressMovable: true, valueFormatter: formatHinbanGenkaDisplayValue, cellStyle: (params) => hinbanGenkaInactiveCellStyle(params, true) },
            ...HINBAN_GENKA_SLOT_DEFS.map((slot) => {
                const meta = resolveHinbanGenkaSlotLayoutMeta(slot.colId);
                const width = Number.isFinite(meta.width) && meta.width > 0 ? meta.width : slot.minWidth;
                return {
                    colId: slot.colId,
                    field: slot.colId,
                    headerName: slot.headerName,
                    minWidth: slot.minWidth,
                    width: width,
                    flex: 1,
                    sortable: false,
                    filter: false,
                    resizable: false,
                    suppressMenu: true,
                    lockPosition: true,
                    suppressMovable: true,
                    valueFormatter: slot.colId === 'kousei' ? formatHinbanGenkaKouseiDisplayValue : formatHinbanGenkaDisplayValue,
                    editable: (params) => isHinbanGenkaCellEditable(String(DicVal(DicVal(params, 'data', {}), 'row_key', '')).trim(), slot.colId),
                    cellStyle: (params) => hinbanGenkaInactiveCellStyle(params, false)
                };
            })
        ];
    }
    function buildHinbanGenkaSummaryRows(sourceRows) {
        const rawRow = rawHinbanGenkaRow(sourceRows);
        return HINBAN_GENKA_SUMMARY_DEFS.map((def) => ({
            rowid: def.id,
            label: def.label,
            value: String(DicVal(rawRow, def.valueKey, '') ?? ''),
            value2: String(DicVal(rawRow, def.valueKey2, '') ?? '')
        }));
    }
    function ensureHinbanGenkaGridDataRef(panelInfo) {
        const dataRef = ensureGridDataRef(panelInfo);
        if (isEmpty(dataRef)) return dataRef;
        const rows = dataRef.current;
        if (!Array.isArray(rows) || rows.length === 0) {
            dataRef.current = [{}];
        }
        return dataRef;
    }

    return {
        normalizeHinbanGenkaUpper,
        mapHinbanGenkaByColumnId,
        emptyHinbanGenkaRow,
        rawHinbanGenkaRow,
        buildHinbanGenkaDisplayRows,
        fixedHinbanGenkaDisplayRows,
        buildHinbanGenkaSaveRows,
        isHinbanGenkaDisplayRows,
        buildHinbanGenkaStoredRows,
        resolveHinbanGenkaSlotLayoutMeta,
        formatHinbanGenkaDisplayValue,
        formatHinbanGenkaKouseiDisplayValue,
        hinbanGenkaSlotColInfo,
        isHinbanGenkaCellEditable,
        hinbanGenkaInactiveCellStyle,
        buildHinbanGenkaFixedColumns,
        buildHinbanGenkaSummaryRows,
        ensureHinbanGenkaGridDataRef
    };
}
